import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import mixpanel from 'mixpanel-browser';
import { BehaviorSubject, filter, Subject, takeUntil } from 'rxjs';
import {
  confirmDeletingModalData,
  confirmEditingModalData,
  podsFormData,
} from '../pods/pods';
import { IPod } from 'src/app/shared/models/item.models';
import { FormService } from 'src/app/shared/components/form/form.service';
import {
  createToaster,
  positionType,
} from 'src/app/shared/components/modals/toaster';
import { PodStore } from '../pod-store';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../firebase/auth.service';
import { ImagesService } from 'src/app/services/images.service';
import { showLoading } from 'src/app/shared/components/modals/loading';
import { TranslationService } from 'src/app/services/translation.service';

@Component({
  selector: 'app-edit-pod',
  templateUrl: './edit-pod.component.html',
  styleUrls: ['./edit-pod.component.scss'],
})
export class EditPodComponent implements OnInit, OnDestroy {
  @Output() isClosePod = new EventEmitter<boolean>(false);
  @ViewChild('podForm') podForm;

  item = { icon: 'radio-button-on', title: 'Edit a Pod' };
  podsFormData = podsFormData;
  id: string;
  disabled: boolean = true;
  newPod: IPod;
  statusDeleting: boolean = false;
  statusAdding: boolean = false;
  pod$ = new BehaviorSubject({} as IPod);
  form: FormGroup;
  initialValues: IPod;
  loader = showLoading();

  currentImage: string;
  loadingImage: boolean = false;

  properties;
  confirmModalData = confirmEditingModalData;
  confirmForDelete = confirmDeletingModalData;
  showConfirmModal: boolean = false;
  showForDelete = false;

  private destroy$ = new Subject<void>();

  currentLang: string;

  constructor(
    private formService: FormService,
    private imagesService: ImagesService,
    private podStore: PodStore,
    private activeRoute: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private translationService: TranslationService
  ) {
    this.translationService.getCurrentLanguage().subscribe(lang => {
      this.currentLang = lang;
    });
  }

  async ngOnInit() {
    this.id = this.activeRoute.snapshot.params.id;
    this.onFormGroupChangePod = this.onFormGroupChangePod.bind(this);
    this.imagesListen();
    this.listenStatusChanges();
    this.podStore.getItemByByValue({
      documentId: this.id,
    });
  }

  ngOnDestroy() {
    this.imagesService.reset();
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackEvent(event) {
    mixpanel.track(event);
  }

  openConfirmModal() {
    this.showConfirmModal = true;
  }

  close(e) {
    e ? (this.showConfirmModal = true) : null;
  }

  discardChanges(e) {
    if (e) {
      this.isClosePod.next(true);
      this.showConfirmModal = false;
    }
  }

  closeModal(e) {
    if (e) {
      this.showConfirmModal = false;
      this.showForDelete = false;
    }
  }

  onFormGroupChangePod(_event) {
    this.form = _event;
    // Add safety check for name control
    const nameControl = _event.get('name');
    if (nameControl) {
      nameControl.setValidators([
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(50),
      ]);
    } else {
      return;
    }

    this.configurePodFormForJoinMechanism(
      _event,
      _event.get('joinMechanism')?.value
    );

    _event.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.configurePodFormForJoinMechanism(_event, value.joinMechanism);
      this.disabled = this.isUpdateDisabled(_event);
      this.newPod = {
        ..._event.getRawValue(),
        image: this.currentImage,
        creator: this.initialValues?.creator,
      };

      // Handle conditional fields based on joinMechanism
      if (value.joinMechanism) {
        this.formService.fieldsConditions.next([value.joinMechanism]);
      }
    });

    // Use stored language value
    if (this.currentLang !== 'en') {
      const nameControl = _event.get('name');
      if (nameControl) {
        nameControl.setValidators([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(50),
        ]);
      }
    }

    // Remove country control access since it's not in the form definition
    // const countryControl = _event.get('country');
    // if (countryControl) {
    //   countryControl.valueChanges.pipe(filter(value => value !== this.initialValues?.country))
    //     .subscribe(() => _event.patchValue({ googleMapLink: null }));
    // } else {
    //   console.log('⚠️ Country control not found in form');
    // }

    this.podStore
      .selectItem()
      .pipe(
        takeUntil(this.destroy$),
        filter(data => !!data)
      )
      .subscribe(data => {
        this.initialValues = data;
        const {
          image,
          joinMechanism,
          joinMechanismArr,
          name,
          googleMapLink,
          description,
          country,
          memberRights,
          approveMembers,
        } = data;

        this.currentImage = image;
        // Only set compressed images if there's actually an image
        if (image) {
          this.imagesService.compressedImages.next([image]);
        } else {
          this.imagesService.compressedImages.next([]);
        }
        this.pod$.next(data);
        _event.patchValue(
          {
            name,
            googleMapLink,
            description,
            memberRights,
            joinMechanism,
            approveMembers,
            joinMechanismArr,
          },
          { emitEvent: false }
        );
        this.configurePodFormForJoinMechanism(_event, joinMechanism);
        _event.markAsPristine();
        this.newPod = {
          ..._event.getRawValue(),
          image: this.currentImage,
          creator: this.initialValues?.creator,
        };
        this.disabled = this.isUpdateDisabled(_event);
        this.formService.location$.next({ googleMapLink });

        // Set initial fields conditions based on joinMechanism
        if (joinMechanism) {
          this.formService.fieldsConditions.next([joinMechanism]);
        }
      });
  }

  configurePodFormForJoinMechanism(form: FormGroup, joinMechanism: string) {
    const questions = form.get('joinMechanismArr') as FormArray;
    if (!questions) return;

    if (joinMechanism === 'request based') {
      if (questions.disabled) {
        questions.enable({ emitEvent: false });
      }
      questions.setValidators(this.atLeastOneRequestQuestion);
    } else {
      questions.clearValidators();
      questions.controls.forEach(group => {
        group.get('memberQuestion')?.setErrors(null);
      });
      if (questions.enabled) {
        questions.disable({ emitEvent: false });
      }
    }

    questions.updateValueAndValidity({ emitEvent: false });
    form.updateValueAndValidity({ emitEvent: false });
  }

  isUpdateDisabled(form: FormGroup = this.form, loading = this.loadingImage) {
    return !form || loading || !form.dirty || form.invalid;
  }

  private atLeastOneRequestQuestion(
    control: AbstractControl
  ): ValidationErrors | null {
    const questions = control as FormArray;
    const invalid = questions.value.every(
      c => c.memberQuestion === null || c.memberQuestion === ''
    );
    questions.controls.forEach(group => {
      const question = group.get('memberQuestion');
      invalid
        ? question?.setErrors({ required: true })
        : question?.setErrors(null);
    });
    return invalid ? { atLeastOneError: true } : null;
  }

  imagesListen() {
    this.imagesService.compressedImages
      .pipe(takeUntil(this.destroy$))
      .subscribe(images => {
        this.currentImage = images[0] || null;
        if (this.form) {
          this.form.markAsDirty();
          this.newPod = {
            ...this.form.getRawValue(),
            image: this.currentImage,
            creator: this.initialValues?.creator,
          };
          this.disabled = this.isUpdateDisabled(this.form);
        }
      });

    this.imagesService.imagesLoading
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.loadingImage = loading;
        this.disabled = this.isUpdateDisabled(this.form, loading);
      });
  }

  async updatePod() {
    if (this.podForm?.form?.valid && !this.disabled && !this.statusAdding) {
      await this.podForm.imageLoader.upLoadImages();
      this.imagesService.imageUrls
        .pipe(takeUntil(this.destroy$))
        .subscribe(urls => {
          if (urls && urls.length) {
            this.setNewItem(urls);
          }
        });
    }
  }

  setNewItem(urls) {
    this.newPod.image = urls.length ? urls[0] : null;
    const newPod = Object.assign({ ...this.initialValues, ...this.newPod });

    if (newPod.joinMechanism === 'automatic') {
      newPod.memberRequests.forEach(request =>
        newPod.participants.push(request.id)
      );
      newPod.memberRequests = [];
      newPod.joinMechanismArr = [];
    }

    this.podStore.updateProfile(newPod);
    this.podStore
      .selectLoading()
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        if (data) {
          this.disabled = true;
          this.statusAdding = true;
        } else {
          this.isClosePod.next(true);
          createToaster(
            'Successfully updated!',
            'success',
            positionType.BOTTOM
          );
          this.disabled = false;
          this.statusAdding = false;
        }
      });
  }

  deletePod(e: boolean) {
    if (e) {
      this.podStore.removeItem(this.id);
      this.podStore
        .selectLoading()
        .pipe(takeUntil(this.destroy$))
        .subscribe(data => {
          this.statusDeleting = !!data;
          if (data) {
            this.disabled = true;
            this.showForDelete = false;
          } else {
            this.isClosePod.next(true);
            createToaster(
              'Successfully deleted!',
              'success',
              positionType.BOTTOM
            );
            setTimeout(() => {
              this.router.navigate(['merfolk/pods']);
            }, 0);
            this.trackEvent('Pod Deleted');
          }
        });
    }
  }

  private listenStatusChanges() {
    this.podStore
      .selectLoading()
      .pipe(takeUntil(this.destroy$))
      .subscribe(async loading => {
        loading ? (await this.loader).present() : (await this.loader).dismiss();
      });
  }
}

